/**
 * Links from emails and texts (a work order, a note, an invoice batch) have to
 * survive the sign-in screen: a signed-out visitor who opens one is shown the
 * login, and after signing in should land on the thing the link pointed at.
 *
 * Only links that name a specific record are remembered. A general address
 * such as `?view=dashboard:location` is not, so a stale bookmark never
 * overrides where a later sign-in would normally go.
 */

/** sessionStorage key holding the address (query string, no `?`) to restore. */
export const POST_LOGIN_REDIRECT_KEY = 'post_login_redirect';

/** Query params that each name one record a link can open. */
export const RESOURCE_DEEP_LINK_PARAMS = ['wo', 'noteId', 'batch'] as const;

/** True when the address points at a specific record (work order, note, invoice batch). */
export const isResourceDeepLink = (params: URLSearchParams): boolean => RESOURCE_DEEP_LINK_PARAMS.some((key) => !!params.get(key));

/** What a record link opens, as the sign-in page names it. */
export type RecordLinkKind = 'workOrder' | 'invoice';

/**
 * What the address opens: an invoice (`batch`) or a work order (`wo`, or a
 * note on one). Null for any other address.
 */
export const recordLinkKind = (search: string | null | undefined): RecordLinkKind | null => {
  if (!search) return null;
  const params = new URLSearchParams(search.replace(/^[?&]/, ''));
  if (params.get('batch')) return 'invoice';
  if (params.get('wo') || params.get('noteId')) return 'workOrder';
  return null;
};

/** What a record link needs to open the record again (view, record ids, department, the Reports tab). */
const RECORD_LINK_KEEP = ['view', 'wo', 'noteId', 'serviceId', 'dept', 'batch', 'tab', 'report'];

/**
 * The record part of the address, for signing out of a login that can't open
 * it and in with the right one: the account and store are dropped, because by
 * then the app has moved them to the signed-in login's own. Null when the
 * address isn't a record link.
 */
export const recordLinkOnly = (search: string): string | null => {
  const params = new URLSearchParams(search.replace(/^[?&]/, ''));
  if (!isResourceDeepLink(params)) return null;
  const kept = new URLSearchParams();
  for (const key of RECORD_LINK_KEEP) {
    const value = params.get(key);
    if (value) kept.set(key, value);
  }
  // Leaving a work order for the dashboard keeps wo in the address: open it as a work order link.
  if (kept.get('wo') && !kept.get('batch')) kept.set('view', 'workorders');
  return kept.toString();
};

/**
 * The query string to remember for after sign-in, or null when the address
 * isn't a record link. `page` is dropped so the restore never re-enters the
 * public page flow.
 */
export const postLoginRedirectFor = (search: string): string | null => {
  const params = new URLSearchParams(search.replace(/^[?&]/, ''));
  if (!isResourceDeepLink(params)) return null;
  params.delete('page');
  return params.toString() || null;
};

/** Remember a record link for after sign-in. Returns true when one was saved. */
export const savePostLoginRedirect = (search: string): boolean => {
  const redirect = postLoginRedirectFor(search);
  if (!redirect) return false;
  try {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, redirect);
    return true;
  } catch {
    // Storage blocked (private mode): the link just won't survive sign-in.
    return false;
  }
};

/**
 * The saved address, removed from storage so it is used once; null when
 * nothing was saved.
 */
export const takePostLoginRedirect = (): URLSearchParams | null => {
  try {
    const saved = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (!saved) return null;
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    return new URLSearchParams(saved.replace(/^[?&]/, ''));
  } catch {
    return null;
  }
};

/** Forget any saved address (sign-out, or the visitor was already signed in). */
export const clearPostLoginRedirect = (): void => {
  try {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  } catch {
    // Storage blocked: nothing was saved either.
  }
};

/**
 * Params that belong to a specific screen or record and must not follow the
 * viewer to the dashboard — left behind, they re-open a work order or an
 * invoice batch on the next screen that reads them.
 */
const DASHBOARD_STALE_PARAMS = ['page', 'wo', 'noteId', 'dept', 'wf', 'q', 'notes', 'batch', 'report', 'report_view'];

/** The address for the dashboard, keeping the account/location the viewer is on. */
export const dashboardSearch = (search: string): string => {
  const params = new URLSearchParams(search.replace(/^[?&]/, ''));
  DASHBOARD_STALE_PARAMS.forEach((key) => params.delete(key));
  // `tab` is the Reports tab (?tab=invoices) unless it names a settings
  // dialog tab (?settings=location&tab=…), which the dashboard still opens.
  if (!params.get('settings')) params.delete('tab');
  params.set('view', 'dashboard');
  return params.toString();
};
